import { getDataURLFromBlob, parseJsonData } from '@frontend/services/helpers';
import { clearCellAngleFit, initDataBuilderTable } from '@frontend/group/modules/simple-table-builder/helpers';
import { fabric } from 'fabric';
import {
  updateDynamicTemplateHeight,
} from '@frontend/utils';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import {EVENTS, simpleTableBuilderEventBroker} from "@frontend/group/modules/simple-table-builder/event-broker";
const getScopePage = () => ShowPageScopeCollector.get(['updateLayoutChanges', 'reorderObjectCanvas']);
const TOP_MOST_HTML_TABLE_ORDER = -1000;
const HTML_TABLE_OVERLAY_PROTECTED_FIELD_TYPES = ['Line'];
const DEFAUL_CELL_VALUE = 'Value';

const getObjectBounds = (object) => {
    if (typeof object.getBoundingRect === 'function') {
        return object.getBoundingRect(true);
    }

    return {
        left: object.left,
        top: object.top,
        width: object.width * (object.scaleX || 1),
        height: object.height * (object.scaleY || 1)
    };
};

const isOverlapping = (a, b) => {
    // Edge-touching counts as overlap because separator lines commonly sit exactly on table edges.
    return !(
        a.left + a.width < b.left ||
        b.left + b.width < a.left ||
        a.top + a.height < b.top ||
        b.top + b.height < a.top
    );
};

const keepOverlappingProtectedFieldsAboveTable = (canvas, image) => {
    // Explicit/user layer order must remain authoritative. This helper only adjusts default table stacking.
    if (image.respectLayerOrder) {
        return;
    }

    const imageBounds = getObjectBounds(image);
    let maxOverlappingProtectedFieldOrder = null;

    for (const object of canvas.getObjects()) {
        if (
            object === image ||
            object.visible === false ||
            !HTML_TABLE_OVERLAY_PROTECTED_FIELD_TYPES.includes(object.fieldType)
        ) {
            continue;
        }

        const order = Number(object.order);
        if (!Number.isFinite(order)) {
            continue;
        }

        if (!isOverlapping(imageBounds, getObjectBounds(object))) {
            continue;
        }

        maxOverlappingProtectedFieldOrder = maxOverlappingProtectedFieldOrder === null
            ? order
            : Math.max(maxOverlappingProtectedFieldOrder, order);
    }

    if (maxOverlappingProtectedFieldOrder !== null) {
        image.set({
            order: Math.max(Number(image.order) || 0, maxOverlappingProtectedFieldOrder) + 1
        });
    }
};

function createHtmlImage(base64data, options, optionsData, canvas, tableObject, data, index, top_position, tableSize = null, templateFieldIdOverride = null) {
    const relatedField = parseJsonData(options['Option2'], {});
    const templateFieldId = templateFieldIdOverride ?? tableObject?.realId ?? options?.id ?? optionsData?.id;
    const usersFieldsOrder = parseJsonData(data?.users_fields_order, []);
    const userLayerOrderIndex = Array.isArray(usersFieldsOrder)
      ? usersFieldsOrder.findIndex(orderId => String(orderId) === String(templateFieldId))
      : -1;
    const hasUserDefinedLayerOrder = userLayerOrderIndex > -1;
    const hasExplicitLayerOrder = Boolean(tableObject?.respectLayerOrder || hasUserDefinedLayerOrder);
    const fieldOrder = parseFloat(options?.Order);
    const resolvedOrder = hasUserDefinedLayerOrder
      ? userLayerOrderIndex + 1
      : (Number.isFinite(fieldOrder) ? fieldOrder : TOP_MOST_HTML_TABLE_ORDER + index);

    return new Promise((resolve, reject) => {
        fabric.Image.fromURL(base64data, function (image) {
          const { updateLayoutChanges, reorderObjectCanvas } = getScopePage();
            try {
                // The blob carries 2px of padding so rotated cell text is not clipped when it is
                // rasterised, so the picture is slightly larger than the table. Box the object by
                // the table's own measurement instead, the way the admin editor does — otherwise
                // the layer reports a width two pixels wider than every other number for it.
                const naturalSize = tableSize?.width > 0 && tableSize?.height > 0
                  ? { width: tableSize.width, height: tableSize.height }
                  : {};

                image.set({
                    ...naturalSize,
                    id: `html_${index}`,
                    element_id: `html_table_${index}`,
                    fieldId: templateFieldId,
                    field_id: optionsData.id,
                    left: +options.X,
                    top: +top_position,
                    angle: parseFloat(options.Angle) || 0,
                    opacity: options.opacity ? options.opacity : 1,
                    fieldType: `HTML Table`,
                    order: resolvedOrder,
                    respectLayerOrder: hasExplicitLayerOrder,
                    tableData: tableObject.fieldOptions,
                    name: options.Name,
                    html_table_data: data.html_tables,
                    relatedField,
                    scaleX: +options.Scale || 1,
                    scaleY: +options.Scale || 1,
                    real_id: tableObject.realId ?? templateFieldId,
                    visible: data['html_tables_visible']?.[index] ?? true,
                    updateLayoutChanges,
                    resizeBySide,
                    resizeByBothDimensions
                });


                // avoid going outside to the right
                const rightSide = Math.round(image.left + image.width + canvas.width * 0.02)
                if (rightSide > canvas.width) {

                  const scale = canvas.width / rightSide
                  image.set({
                    scaleX: image.scaleX - (1 - scale),
                    scaleY: image.scaleY - (1 - scale),
                  })
                }

                canvas.add(image);

                // Which handle is being dragged; nothing is written until the drop.
                image.on('scaling', function () {
                  this._resizeCorner = this.__corner || this._resizeCorner;
                });

                image.on('modified', function () {
                  const corner = this.__corner || this._resizeCorner || '';
                  this._resizeCorner = null;

                  const syncWidth = WIDTH_RESIZE_CORNERS.includes(corner);
                  const syncHeight = HEIGHT_RESIZE_CORNERS.includes(corner);

                  if (!syncWidth && !syncHeight) return;

                  syncTableSize(this, { syncWidth, syncHeight });
                });

                image.setCoords();
                keepOverlappingProtectedFieldsAboveTable(canvas, image);


                if (reorderObjectCanvas) {
                  reorderObjectCanvas(canvas);
                }

                canvas.renderAll();
                resolve(image); // Resolve the promise with the image
            } catch (error) {
                reject(error); // Reject in case of errors
            }
        });
    });
}

function incrementFontSize(data) {
  const incrementSize = 5;
  const incrementPx = (item) => {
    if (
      item?.styles?.rules &&
      typeof item.styles.rules["font-size"] === "string"
    ) {
      const sizeStr = item.styles.rules["font-size"];
      const value = parseInt(sizeStr, 10);
      if (!isNaN(value)) {
        item.styles.rules["font-size"] = (value + incrementSize) + "px";
        data.autoFontSizeValue = value;
        clearCellAngleFit(item.styles.rules);
      }
    }
  };

  data.headers?.forEach(incrementPx);
  data.rows?.forEach(row => {
    incrementPx(row); // apply to the row itself
    row.cells?.forEach(incrementPx); // apply to each cell in the row
  });

  return data;
}


/**
 * Grows the font 5px at a time until the table outgrows the size it started at, then steps back to
 * the last one that fit. Shared by the initial render and the re-render after a resize, so both
 * settle on the same font.
 */
async function applyAutoFontSize(tableObject, tableOuterDimension, previewId) {
  const originalWidth = tableOuterDimension.width;
  const originalHeight = tableOuterDimension.height;
  let previousTableObject = tableObject;
  let retries = 0;

  while (tableOuterDimension.height <= originalHeight && tableOuterDimension.width <= originalWidth) {
    retries++;
    // create a deep copy
    previousTableObject = JSON.parse(JSON.stringify(tableObject));
    tableObject = incrementFontSize(tableObject);
    tableOuterDimension = await initDataBuilderTable(tableObject).getOuterDimension();

    // just in case so we don't get stuck in the loop
    if (retries > 10) {
      console.log("Maximize font size has reached the max number of tries ");
      return tableObject;
    }
  }

  simpleTableBuilderEventBroker.fire(EVENTS.UPDATE_AUTO_FONT_SIZE_VALUE, {
    previewId,
    tableId: previousTableObject.id,
    autoFontSizeValue: previousTableObject.autoFontSizeValue
  });

  return previousTableObject;
}

// checks if all the rows have the defaul value "Value"
function isEmptyTable(rows) {
    return rows.every(row =>
        !Array.isArray(row.cells) ||
        row.cells.every(cell =>
            (!cell.value || cell.value === DEFAUL_CELL_VALUE)
            && !/<img\b[^>]*\bsrc\s*=\s*["'][^"']+["']/i.test(cell.valueHtml || '')
        )
    );
}

function isRowsEmpty(tableObject) {
    
    if (!tableObject.rows) {
      return true;
    }

    if (Array.isArray(tableObject.rows) && tableObject.rows.length === 0) {
      return true
    }

    // for now I will use this validation just for column-mode Tables
    if (tableObject.tableColumnMode && isEmptyTable(tableObject.rows)){
      return true;
    }

    return Object.keys(tableObject.rows).length === 1 && tableObject.rows["-1"];
}

export async function drawHtml(params, retries = 0) {
  return new Promise(async (resolve) => {
    const { canvas, data, id } = params;
    const htmlTemplateFields = (params?.fields || []).filter((field) => field.type === 'HTML');

    // Remove old HTML objects
    canvas.getObjects().forEach((o) => {
      if (o.id?.startsWith('html')) {
        canvas.remove(o);
      }
    });

    if (data.html_tables?.length) {
      const tablePromises = []; // Array to track all table promises

      for (const [index, o] of data.html_tables.entries()) {
        let tableObject = parseJsonData(o, null);
        if (!tableObject) {
          console.log('Error parsing table JSON data:', o);
          continue;
        }

        if (isRowsEmpty(tableObject)){
            continue; 
        }

        const options = parseJsonData(tableObject.fieldOptions, null);
        if (!options) continue; //ignore empty table
        const optionsData = parseJsonData(options['Option1'], null);
        let table = initDataBuilderTable(tableObject);
        let tableOuterDimension = await table.getOuterDimension();
        table.styles.setRules({
          'width': `${tableOuterDimension.width}px`,
          'height': `${tableOuterDimension.height}px`,
        });
        let blob;

        // Maximize the font size until the point it will not increaze the original table size
        if (tableObject.autoFontSize) {
          tableObject = await applyAutoFontSize(tableObject, tableOuterDimension, id);
        }

        table = initDataBuilderTable(tableObject);

        if (table.angleFitApplied) {
          const displayWidth = parseFloat(options.Width);

          if (Number.isFinite(displayWidth) && displayWidth > 0 && tableOuterDimension.width > 0) {
            options.Height = Math.round(tableOuterDimension.height * displayWidth / tableOuterDimension.width);
          }

          // Auto font size grew the font in this working copy only — the stored table keeps its
          // base font and just the autoFontSizeValue, so writing this copy back would make the
          // next render start from the grown font and grow again.
          if (!tableObject.autoFontSize) {
            data.html_tables[index] = JSON.stringify(Object.assign({}, tableObject, table, {
              fieldOptions: JSON.stringify(options),
            }));
          }
        }

        blob = await table.toBlob();
        const reader = new FileReader();

        // Create a promise for each table's processing
        const tablePromise = new Promise((tableResolve, tableReject) => {
          reader.readAsDataURL(blob);
          reader.onloadend = async function () {
            const base64data = reader.result;

            let top_position = options.Y;

            // POR-13569 - Removing this logic from the frontend because it is no longer needed.
            // Related field positioning is now fully handled during the import process, making
            // this code redundant. In addition, this implementation can create duplicate tables,
            // misposition some tables, and adds unnecessary processing overhead by waiting for
            // related elements to be rendered. Keeping the code here commented out temporarily
            // for reference in case we need to restore or compare the previous behavior.

            /*
            // Relative field positioning
            var related_field_type = "";
            var related_field_name = "";
            var margin = 5;
            var move_down_only = false;

            try {
              let parsedOption = parseJsonData(options['Option2'], null);

              if (parsedOption && parsedOption.field && parsedOption.field.trim() !== "") {
                related_field_name = parsedOption.field;
              }

              if (parsedOption && parsedOption.fieldType && parsedOption.fieldType.trim() !== "") {
                related_field_type = parsedOption.fieldType;
              }

              // this comes from MarkupFieldBuilderService
              if (options['related_field_name'] && options[related_field_name]!=''){
                related_field_name = options['related_field_name'];
                related_field_type = null;
              }

              if (parsedOption && parsedOption.margin !== undefined) {
                margin = parsedOption.margin;
              }
              if (parsedOption && parsedOption.move_down_only !== undefined) {
                move_down_only = parsedOption.move_down_only;
              }
            } catch (e) {
              // Handle JSON parsing error
            }
            
            if (related_field_name) {
              let skip_related_field = false;

              if (related_field_type == "HTML"){
                for (const related_o of data.html_tables) {
                  let relatedTableObject = parseJsonData(related_o, null);
                  if (!relatedTableObject) {
                    console.log('Error parsing table JSON data:', related_o);
                    continue;
                  }
                  const related_options = parseJsonData(relatedTableObject.fieldOptions, null);
                  if (!related_options) continue; //ignore empty table

                  // found the table, lets check if it has any data...
                  if (related_options.Name && related_options.Name === related_field_name) {
                    if (isRowsEmpty(relatedTableObject)){
                      skip_related_field = true;
                    }
                    // we found it ... exit the for loop
                    break;
                  }
                }
              }

              if (!skip_related_field) {

              var canvasObjects = canvas.getObjects();
              var related_field = null;

              if (related_field_type === 'Icons') {
                related_field = getIconsRelatedField(data.icons, related_field_name);
              } else {
                canvasObjects.forEach((obj) => {
                  if (obj.templateField && obj.templateField.name === related_field_name) {
                    related_field = obj;
                  } else if (obj.name && obj.name === related_field_name) {
                    related_field = obj;
                  } else if (obj.element_id && obj.element_id === related_field_name){
                    related_field = obj;
                  }
                });
              }

              if (related_field ) {
                if (related_field_type !== 'Markup Field') {

                  // check that these values are set and are valid numbers .. 
                  // this happens when the template is split into multiple templates using 
                  // the template overflow handler service... for now we can validate it here.

                  if (related_field?.top != null && !Number.isNaN(Number(related_field.top)) &&
                      related_field?.height != null && !Number.isNaN(Number(related_field.height))) {
                        top_position = related_field.top + related_field.height + margin;
                  }

                  if (move_down_only && top_position < options.Y) {
                    top_position = options.Y;
                  }
                }
              } else {
                // Retry logic
                await new Promise((r) => setTimeout(r, 400));
                if (retries < 3) {
                  await drawHtml(params, retries + 1); // Use await to avoid unresolved promises
                  return tableResolve(); // Ensure the promise resolves after retry
                } else {
                  tableResolve(); // Resolve even if the retries fail after max attempts
                }
              }
              }
            }

            */

            createHtmlImage(
              base64data,
              options,
              optionsData,
              canvas,
              tableObject,
              data,
              index,
              top_position,
              tableOuterDimension,
              htmlTemplateFields[index]?.id ?? null
            )
              .then( async (image) =>  {
                let bottom_margin = 20;
                let new_template_height = Math.round(image.top + image.height + bottom_margin);
                if (new_template_height > Math.round(params.canvas.height) ) {
                  const formData = new FormData();
                  formData.append('new_height', new_template_height);
                  await updateDynamicTemplateHeight(formData, params.id);
                  ShowPageScopeCollector.set({ NeedToRedrawEverything: true });
                }
                tableResolve(); // Resolve this table's processing
              })
              .catch((error) => {
                console.error("Error:", error);
                tableReject(error); // Reject if there's an error
              });

          };
        });

        tablePromises.push(tablePromise); // Add promise to the array
      }

      // Wait for all tables to finish processing
      await Promise.all(tablePromises);
    }

    // Resolve the main promise
    resolve();
  });
}

/**
 *
 * @param {Array} icons
 * @param {string} relatedFieldName
 * @return {null|Object}
 */
function getIconsRelatedField(icons, relatedFieldName) {
  if (!icons || !icons.length) return null;
  /** @var {Object} group */
  let group = icons.find(icon => icon?.field_name === relatedFieldName) ?? null;

  if (!group?.elements || !group?.elements?.length) {
    return null;
  }

  let relatedField = {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  };

  group.elements.forEach(icon => {
    if ((icon.top + icon.height) > (relatedField.top + relatedField.height)) {
      relatedField.top = icon.top;
      relatedField.height = icon.height;
    }
    if ((icon.left + icon.width) > (relatedField.left + relatedField.width)) {
      relatedField.left = icon.left;
      relatedField.width = icon.width;
    }
  })

  return relatedField;
}

const WIDTH_RESIZE_CORNERS = ['tl', 'tr', 'bl', 'br', 'ml', 'mr'];
const HEIGHT_RESIZE_CORNERS = ['tl', 'tr', 'bl', 'br', 'mt', 'mb'];

/**
 * Writes the size the user just asked for into the table itself, so the next render rasterises a
 * table of that size instead of stretching the previous image. Only the axes that actually
 * changed — the other one keeps what it had, `auto` included.
 */
function syncTableSize(image, { syncWidth = true, syncHeight = true } = {}) {
  const preview = window.previews?.find(p => p.id === image.canvas?.instance_id);
  const html_tables = preview?.data?.html_tables;
  const tableIndex = image.id.split('_').pop();

  if (!html_tables?.[tableIndex] || typeof image.updateLayoutChanges !== 'function') return;

  const tableData = parseJsonData(html_tables[tableIndex], null);

  if (!tableData?.styles?.rules) return;

  const width = image.getScaledWidth();
  const height = image.getScaledHeight();
  const nextWidth = `${Math.round(width)}px`;
  const nextHeight = `${Math.round(height)}px`;
  const rules = tableData.styles.rules;

  // Committing a transform-panel field without changing it still fires `change`, and a handle can
  // be released where it was grabbed — neither is worth re-rasterising the table for.
  if ((!syncWidth || rules.width === nextWidth) && (!syncHeight || rules.height === nextHeight)) return;

  if (syncWidth) rules.width = nextWidth;
  if (syncHeight) rules.height = nextHeight;

  const copyTables = structuredClone(html_tables);
  copyTables[tableIndex] = JSON.stringify(tableData);

  image.updateLayoutChanges(image.canvas.instance_id, 'html_tables', copyTables);
  // The object carries its own copy of the tables, and that is what the Table Builder opens and
  // saves from — leaving it stale would show the old size and undo the resize on save.
  image.html_table_data = copyTables;

  rerenderTableImage(image, tableData);
}

/**
 * Re-rasterises this one table at its new size. Swapping the source in place keeps the object,
 * its position and the selection — a full drawHtml removes and re-adds every table on the canvas.
 */
async function rerenderTableImage(image, tableData) {
  try {
    let table = initDataBuilderTable(tableData);

    // The box just changed, so the font has to be re-settled against the new one.
    if (tableData.autoFontSize) {
      const measured = await table.getOuterDimension();
      const resized = await applyAutoFontSize(tableData, measured, image.canvas?.instance_id);

      table = initDataBuilderTable(resized);
    }

    const { width, height } = await table.getOuterDimension();
    const base64data = await getDataURLFromBlob(await table.toBlob());

    // Drawn at the table's own measured size, so a table that could not take the size asked of it
    // comes back at the size it can actually be instead of staying squashed. setSrc would
    // otherwise adopt the blob's size, which is 2px larger — that padding is what keeps rotated
    // cell text from being clipped when it is rasterised.
    image.setSrc(base64data, () => {
      image.set({ width, height, scaleX: 1, scaleY: 1 });
      image.setCoords();
      image.canvas?.requestRenderAll();
    });
  } catch (e) {
    console.error('Error re-rendering the resized table: ' + e.message);
  }
}

/**
 * Resize a fabric.Image object by width or height,
 * keeping aspect ratio and position.
 *
 * @param {"width"|"height"} side - side to resize
 * @param {number} value          - new size in px
 * @param {boolean} isNeedUpdateData - ability to resize with or without data refresh.
 */
function resizeBySide(side, value, isNeedUpdateData = true) {
  try {
    if (!(this.width && this.height)) return;

    // natural dimensions (before scaling)
    const natW = this.width;
    const natH = this.height;

    // store current left top point
    const leftTopPoint = this.getPointByOrigin('left', 'top');

    let scale;
    if (side === "w") {
      scale = value / natW;
    } else if (side === "h") {
      scale = value / natH;
    } else {
      throw new Error('side must be "w" or "h"');
    }

    this.set({
      scaleX: scale, scaleY: scale,
    });

    // restore previous center
    this.setPositionByOrigin(leftTopPoint, "left", "top");
    this.setCoords();

    this.canvas?.requestRenderAll();

    if(!isNeedUpdateData) {
      return;
    }

    // Scaling is uniform here, so both axes moved.
    syncTableSize(this);
  } catch (e) {
    console.error('Error in resizeBySide: '+ e.message());
  }
}
   /**
 * Resize a fabric.Image object by width and height,
 * 
 * @param {"width"}  targetWidth   -  new width in px
 * @param {"height"} targetHeight   -  new height in px
 * @param {boolean} isNeedUpdateData - ability to resize with or without data refresh.
 */

function resizeByBothDimensions(targetWidth, targetHeight, isNeedUpdateData = true, changedAxis = null) {
  try {
    if (!(this.width && this.height)) return;

    // natural dimensions (before scaling)
    const natW = this.width;
    const natH = this.height;

    // store current left-top point
    const leftTopPoint = this.getPointByOrigin('left', 'top');

    // calculate independent scales
    const scaleX = targetWidth / natW;
    const scaleY = targetHeight / natH;

    this.set({
      scaleX,
      scaleY,
    });

    // restore previous position
    this.setPositionByOrigin(leftTopPoint, 'left', 'top');
    this.setCoords();

    this.canvas?.requestRenderAll();

    if (!isNeedUpdateData) {
      return;
    }

    // Editing one field of the transform panel must not pin the other axis — an untouched
    // Height stays `auto`.
    syncTableSize(this, {
      syncWidth: changedAxis !== 'h',
      syncHeight: changedAxis !== 'w',
    });
  } catch (e) {
    console.error('Error in resizeBySize: ' + e.message);
  }

}
